import { FC, useMemo } from 'react';
import { Button, Typography } from 'antd';
import { TbFileText } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import CompactVirtualizedList from '@/shared/components/lists/CompactVirtualizedList';
import {
    createFinancialFilingHref,
    resolveFinancialFilingPathname,
} from '@/shared/components/tables/FinancialSourceAttribution';
import type { FinancialSourceAttribution } from '@/shared/components/tables/FinancialSourceAttribution';
import MiddleDot from '@/shared/components/typography/MiddleDot';
import { dayjs } from '@/utilities';

import type { FinancialOverviewCorporateDebtNote } from '../../types';
import { getDebtNoteMetadata, orderDebtNotesByMaturity } from './debtInstrumentPresentation';

interface DebtInstrumentsListProps {
    notes: FinancialOverviewCorporateDebtNote[];
    tickerSymbol?: string;
}

const timeToMaturity = (note: FinancialOverviewCorporateDebtNote) => {
    const maturity = note.maturityDate && dayjs(note.maturityDate).isValid() ? dayjs(note.maturityDate) : undefined;

    if (!maturity) {
        return undefined;
    }

    const months = maturity.diff(dayjs(), 'month', true);

    if (months < 0) {
        return `Matured ${Math.max(1, Math.round(Math.abs(months)))} months ago`;
    }

    return months >= 24
        ? `${(months / 12).toFixed(1)} years remaining`
        : `${Math.max(1, Math.round(months))} months remaining`;
};

const DEBT_FACT_GRID_STYLE = {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: '5.5rem 7rem minmax(8rem, 1fr) 4.5rem',
    width: '27.25rem',
} as const;

const DebtInstrumentsList: FC<DebtInstrumentsListProps> = ({ notes, tickerSymbol }) => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const filingsPathname = useMemo(() => resolveFinancialFilingPathname(location.pathname), [location.pathname]);
    const orderedNotes = useMemo(() => orderDebtNotesByMaturity(notes), [notes]);

    const filingHref = (note: FinancialOverviewCorporateDebtNote) => {
        if (!filingsPathname) {
            return undefined;
        }

        const attribution: FinancialSourceAttribution = {
            acceptedDate: note.acceptedDate ?? undefined,
            cik: note.cik ?? undefined,
            filingDate: note.filingDate ?? undefined,
            filingText: note.title,
            filingType: note.filingType ?? '10-K',
            filingUrl: note.filingUrl ?? undefined,
            lineItemLabel: note.title,
            symbol: note.symbol ?? tickerSymbol,
        };

        return createFinancialFilingHref(location.pathname, attribution, location.search);
    };

    return (
        <CompactVirtualizedList
            actions={
                <div style={DEBT_FACT_GRID_STYLE}>
                    {['Coupon', 'Maturity', 'Time To Maturity'].map((label) => (
                        <Typography.Text
                            key={label}
                            style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}
                        >
                            {label}
                        </Typography.Text>
                    ))}
                    <span aria-hidden='true' />
                </div>
            }
            animated={false}
            collapsible={false}
            dataSource={orderedNotes}
            estimateSize={76}
            getItemKey={(note, index) => `${note.cik ?? note.symbol ?? 'note'}-${note.title}-${index}`}
            headerStyle={{ padding: '0.375rem 0.75rem' }}
            icon={<TbFileText size='0.875rem' />}
            listHeight={Math.min(notes.length * 76, 16 * 22)}
            listStyle={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}
            stickyHeader={false}
            style={{ border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius, overflow: 'hidden' }}
            title='Reported Debt Instruments'
            renderItem={(note) => {
                const href = filingHref(note);
                const maturity = timeToMaturity(note);
                const metadata = getDebtNoteMetadata(note);
                const maturityLabel =
                    note.maturityDate &&
                    dayjs(note.maturityDate).isValid() &&
                    note.maturityDate !== `${note.maturityYear}-12-31`
                        ? dayjs(note.maturityDate).format('MMM D, YYYY')
                        : note.maturityYear
                          ? String(note.maturityYear)
                          : '—';
                const facts = [note.couponRate !== null ? `${note.couponRate}%` : '—', maturityLabel, maturity ?? '—'];

                return (
                    <CompactVirtualizedList.Item
                        leading={
                            <span
                                style={{
                                    alignItems: 'center',
                                    background: token.colorBgBase,
                                    border: `1px solid ${token.colorBorderSecondary}`,
                                    borderRadius: token.borderRadiusSM,
                                    color: token.colorTextTertiary,
                                    display: 'inline-flex',
                                    height: '1.75rem',
                                    justifyContent: 'center',
                                    width: '1.75rem',
                                }}
                            >
                                <TbFileText size='1rem' />
                            </span>
                        }
                        style={{ alignItems: 'center', padding: '0.625rem 0.75rem' }}
                    >
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '0.75rem 1.5rem',
                                minWidth: 0,
                                width: '100%',
                            }}
                        >
                            <div style={{ flex: '1 1 22rem', minWidth: 0 }}>
                                <Typography.Text ellipsis style={{ display: 'block', fontWeight: 500 }}>
                                    {note.title}
                                </Typography.Text>
                                {metadata.length ? (
                                    <div
                                        style={{
                                            color: token.colorTextTertiary,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            marginTop: '0.25rem',
                                        }}
                                    >
                                        {metadata.map((item, index) => (
                                            <small key={`${item}-${index}`}>
                                                {index ? <MiddleDot horizontalMargin='0.375rem' /> : null}
                                                {item}
                                            </small>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                            <div
                                style={{
                                    alignItems: 'center',
                                    flex: '0 1 auto',
                                    ...DEBT_FACT_GRID_STYLE,
                                }}
                            >
                                {facts.map((value, index) => (
                                    <Typography.Text ellipsis key={`${value}-${index}`} style={{ display: 'block' }}>
                                        {value}
                                    </Typography.Text>
                                ))}
                                <Button
                                    aria-label={`View ${note.title} SEC filing`}
                                    disabled={!href}
                                    icon={<TbFileText style={{ verticalAlign: '-0.125rem' }} />}
                                    onClick={() => href && navigate(href)}
                                    size='small'
                                    type='text'
                                >
                                    View
                                </Button>
                            </div>
                        </div>
                    </CompactVirtualizedList.Item>
                );
            }}
        />
    );
};

export default DebtInstrumentsList;
